import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { BrandLogo } from '@/components/shared/brand-logo';
import type { FooterConfig } from '@comp-sys/shared';

interface ConfigurableFooterProps {
  variant: 'landing' | 'dashboard';
  config: FooterConfig;
}

export function ConfigurableFooter({ variant, config }: ConfigurableFooterProps) {
  const { copyrightText, icpFiling, linkGroups } = config;

  if (variant === 'dashboard') {
    return (
      <footer className="px-3 pb-3 md:px-4 md:pb-4 lg:px-6 lg:pb-6 pt-4 text-center">
        <p className="text-xs text-muted-foreground">
          {copyrightText}
          {icpFiling.enabled && icpFiling.number && (
            <>
              {' | '}
              <a
                href={icpFiling.url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                {icpFiling.number}
              </a>
            </>
          )}
        </p>
      </footer>
    );
  }

  // Landing variant
  return (
    <footer className="border-t border-surface bg-overlay/50 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className={cn(
            linkGroups.length === 0 && 'md:col-span-4',
            linkGroups.length === 1 && 'md:col-span-3',
            linkGroups.length === 2 && 'md:col-span-2',
            linkGroups.length === 3 && 'md:col-span-1',
          )}>
            <BrandLogo variant="light" size="md" linkTo="/" className="mb-4" />
          </div>

          {/* Link columns */}
          {linkGroups.map((group) => (
            <div key={group.id}>
              <h4 className="font-semibold text-sm mb-4">{group.title}</h4>
              <ul className="space-y-2">
                {group.links.map((link, idx) => (
                  <li key={idx}>
                    {link.openInNewTab ? (
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.url}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Copyright + ICP */}
        <div className="mt-10 pt-6 border-t border-surface text-center">
          <p className="text-xs text-muted-foreground">
            {copyrightText}
            {icpFiling.enabled && icpFiling.number && (
              <>
                {' | '}
                <a
                  href={icpFiling.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary transition-colors"
                >
                  {icpFiling.number}
                </a>
              </>
            )}
          </p>
        </div>
      </div>
    </footer>
  );
}
